// Anchor → "Pick from your own library". Three sub-modes:
//   · select — search/pick an existing substance from the Substance Library
//   · chroma — add a NEW substance from chromatograms (+ experiment config)
//   · import — import an existing flavor formulation (paste / spreadsheet)
// Ported from the Claude Design prototype (anchor-target.jsx, OwnLibraryMode).

import { Icon } from "@/components/Icon/Icon";
import { FormulationImport } from "@/components/FormulationImport";
import { cn } from "@/utils/cn";
import type { IconName } from "@/utils/types/ui";
import { ChromatogramMode } from "./ChromatogramMode";
import { RequiredStar } from "./ChromatogramFiles";
import { LibrarySelect } from "./LibrarySelect";
import type { Anchor, LibMode } from "./anchorTypes";

const LIB_SUBMODES: { id: LibMode; label: string; icon: IconName }[] = [
  { id: "select", label: "Your substances", icon: "search" },
  { id: "chroma", label: "Add new substance", icon: "molecule" },
  { id: "import", label: "Import formulation", icon: "table" },
];

type OwnLibraryModeProps = {
  draftId: string;
  value: Anchor;
  set: (patch: Partial<Anchor>) => void;
  onDuplicateNameChange?: (isDuplicate: boolean) => void;
  onOpenLibrary: () => void;
};

export function OwnLibraryMode({
  draftId,
  value,
  set,
  onDuplicateNameChange,
  onOpenLibrary,
}: OwnLibraryModeProps) {
  const libMode = value.libMode;

  return (
    <div>
      {/* Secondary underline tabs */}
      <div
        className="hstack"
        style={{ gap: 18, borderBottom: "1px solid var(--line)", marginBottom: 16 }}
      >
        {LIB_SUBMODES.map((t) => {
          const sel = libMode === t.id;
          return (
            <button
              key={t.id}
              type="button"
              className={cn("lib-subtab", sel && "active")}
              onClick={() => set({ libMode: t.id })}
            >
              <Icon name={t.icon} size={12} />
              {t.label}
            </button>
          );
        })}
      </div>

      {libMode === "select" && (
        <LibrarySelect
          libSearch={value.libSearch}
          onLibSearchChange={(v) => set({ libSearch: v })}
          pickedLibId={value.pickedLibId}
          onPick={(picked) =>
            set({
              pickedLibId: picked?.id ?? null,
              pickedLibRef: picked
                ? { ndbTable: picked.ndbTable, rowId: picked.rowId, name: picked.name }
                : null,
            })
          }
          onOpenLibrary={onOpenLibrary}
        />
      )}

      {libMode === "chroma" && (
        <ChromatogramMode
          draftId={draftId}
          nameLabel="Name this substance"
          namePlaceholder="e.g. Alphonso mango pulp · 2024 batch"
          referenceName={value.newName}
          onReferenceNameChange={(v) => set({ newName: v })}
          chromas={value.chromas}
          onChromasChange={(list) => set({ chromas: list })}
          chromaConfigById={value.chromaConfigById}
          onChromaConfigByIdChange={(next) => set({ chromaConfigById: next })}
          onDuplicateNameChange={onDuplicateNameChange}
        />
      )}

      {libMode === "import" && (
        <div className="vstack gap-3">
          <div className="vstack gap-2">
            <label className="hstack" style={{ alignItems: "baseline" }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>
                Name this flavor
                <RequiredStar />
              </span>
            </label>
            <input
              type="text"
              value={value.newName}
              onChange={(e) => set({ newName: e.target.value })}
              placeholder="e.g. Strawberry Cream base v3"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 6,
                border: "1px solid var(--line)",
                background: "var(--bg)",
                fontSize: 13,
                outline: "none",
              }}
            />
            <span style={{ fontSize: 11, color: "var(--ink-4)", lineHeight: 1.5 }}>
              We'll save the imported formulation to your library under this name.
            </span>
          </div>
          <FormulationImport
            value={value.formulaImport}
            onChange={(v) => set({ formulaImport: v })}
            onOpenLibrary={onOpenLibrary}
          />
        </div>
      )}
    </div>
  );
}
